import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, InputBase, Button, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';

/** Width of the centred input column in discovery mode. */
export const AI_FIELD_MAX_WIDTH = 640;

interface AISearchFieldProps {
    onSubmit: (query: string) => void;
    loading?: boolean;
    mode?: 'discovery' | 'followup';
    /**
     * Renders the "View all chats" link below the field.
     *
     * DiscoverView sets this to false — it hoists the link into the greeting
     * row instead, which saves a line of vertical space for the Flow canvas.
     * Defaults to true so other call sites keep their existing behaviour.
     */
    showAllChatsLink?: boolean;
}

export function AISearchField({
    onSubmit,
    loading = false,
    mode = 'discovery',
    showAllChatsLink = true,
}: AISearchFieldProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [focused, setFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const isDark = theme.palette.mode === 'dark';
    const hasText = query.trim().length > 0;

    const handleSubmit = useCallback(() => {
        const trimmed = query.trim();
        if (!trimmed || loading) return;
        onSubmit(trimmed);
        setQuery('');
    }, [query, loading, onSubmit]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const borderColor = focused
        ? theme.palette.primary.main
        : theme.palette.divider;

    const isDiscovery = mode === 'discovery';

    return (
        <Box
            sx={{
                maxWidth: isDiscovery ? AI_FIELD_MAX_WIDTH : undefined,
                mx: isDiscovery ? 'auto' : 0,
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1.25,
                    px: 1.75,
                    py: '7px',
                    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03),
                    border: 1,
                    borderColor: borderColor,
                    borderRadius: '6px',
                    transition: 'border-color 0.15s',
                }}
            >
                <InputBase
                    inputRef={inputRef}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onKeyDown={handleKeyDown}
                    multiline
                    minRows={2}
                    maxRows={8}
                    placeholder={
                        mode === 'followup'
                            ? 'Ask a follow-up...'
                            : 'Ask about events, assets, or market signals…'
                    }
                    sx={{
                        flex: 1,
                        fontSize: '14px',
                        '& textarea::placeholder': {
                            color: 'text.disabled',
                            opacity: 1,
                        },
                    }}
                />
                {loading ? (
                    <CircularProgress
                        size={14}
                        sx={{ color: 'text.disabled', mb: '5px' }}
                    />
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSubmit}
                        disabled={!hasText}
                        sx={{
                            minWidth: 'unset',
                            px: 1,
                            py: 0.5,
                            opacity: hasText ? 1 : 0.5,
                            fontSize: '12px',
                            lineHeight: 1,
                        }}
                    >
                        <SendIcon sx={{ fontSize: 14 }} />
                    </Button>
                )}
            </Box>

            {showAllChatsLink && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 1,
                    }}
                >
                    <ViewAllChatsLink onClick={() => navigate('/chats')} />
                </Box>
            )}
        </Box>
    );
}

/**
 * Extracted so DiscoverView can render the same link in its greeting row
 * without duplicating the styling.
 */
export function ViewAllChatsLink({ onClick }: { onClick: () => void }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '11px',
                color: 'text.secondary',
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:hover': {
                    color: 'text.primary',
                },
            }}
        >
            <ChatIcon sx={{ fontSize: 13 }} />
            View all chats
        </Box>
    );
}